import { v4 } from 'uuid';
import { navStore } from '@mcn-platform/models';

export const deployOrganizationAction = {
  name: 'deploy',
  label: 'Deploy',
  action: (organization: { id: string; name?: string }, navigate: unknown, store: {
    deployOrganization: (org: { id: string; name?: string }) => Promise<void>;
  }) => {
    navStore.showConfirmDialog(
      'Deploy Organization',
      `Are you sure you want to deploy organization ${organization.name}?`,
      async () => {
        await store.deployOrganization(organization);
      },
      'Deploy Organization',
      undefined,
      'Cancel',
    );
  },
  disabled: () => false,
};

export const addMeAsSuperUserAction = {
  name: 'addMeAsSuperUser',
  label: 'Add Me as Super User',
  action: (organization: { id: string; name?: string }, navigate: unknown, store: {
    currentUserRoles: string[];
    currentUserOrganizations: Array<{
      organization?: { id: string };
      superUser?: boolean;
      roles?: string[];
      updateValues: (v: { superUser: boolean; roles: string[] }) => void;
    }>;
    currentUser: { email: string };
    add: (model: string, data: unknown) => Promise<void>;
    addToCurrentOrganizations: (id: string) => void;
    addToCurrentUserOrganizations: (id: string) => void;
    update: (model: string, item: unknown) => Promise<void>;
  }) => {
    const isCurrentOrgSuperUser = store.currentUserRoles.includes('SUPERUSER');
    const existingUserOrg = store.currentUserOrganizations.find(
      (userOrg) => userOrg.organization?.id === organization.id,
    );

    if (!isCurrentOrgSuperUser) {
      navStore.showErrorDialog(
        'Access Denied',
        'You must be a super user on your current organization to perform this action.',
      );
      return;
    }

    if (existingUserOrg?.superUser) {
      navStore.showSnackBar('You are already a super user for this organization.', 'success');
      return;
    }

    if (existingUserOrg) {
      navStore.showConfirmDialog(
        'Confirm Action',
        `Make yourself a super user for "${organization.name}"?`,
        async () => {
          try {
            const roles = Array.isArray(existingUserOrg.roles)
              ? [...new Set([...existingUserOrg.roles, 'FULLADMIN'])]
              : ['FULLADMIN'];
            existingUserOrg.updateValues({ superUser: true, roles });
            await store.update('userOrganization', existingUserOrg);
            navStore.showSnackBar('You are now a super user for this organization.', 'success');
          } catch (error) {
            console.error('Error updating UserOrganization:', error);
            navStore.showErrorDialog('Error', 'Failed to make you a super user. Please try again.');
          }
        },
        'Make Super User',
        undefined,
        'Cancel',
      );
      return;
    }

    navStore.showConfirmDialog(
      'Confirm Action',
      `Are you sure you want to add yourself as a super user to "${organization.name}"?`,
      async () => {
        try {
          const userOrganization = {
            id: v4(),
            organization,
            name: store.currentUser.email,
            user: store.currentUser,
            superUser: true,
            roles: ['FULLADMIN'],
          };
          await store.add('userOrganization', userOrganization);
          store.addToCurrentOrganizations(organization.id);
          store.addToCurrentUserOrganizations(userOrganization.id);
          navStore.showSnackBar('You have been added as a super user to this organization.', 'success');
        } catch (error) {
          console.error('Error creating UserOrganization:', error);
          navStore.showErrorDialog('Error', 'Failed to add you as a super user. Please try again.');
        }
      },
      'Add Me',
      undefined,
      'Cancel',
    );
  },
  disabled: (_organization: unknown, store: { currentUserRoles: string[] }) => (
    !store.currentUserRoles.includes('SUPERUSER')
  ),
};
